// 업데이트 소식(What's New) — "뭐가 바뀌었지?"를 앱 안에서 알려주는 창구.
//
// 소식의 진본은 우리 서버가 아니라 **ScreenChat**(screen-chat.com)이다. 개선 요청이 거기서
// 티켓이 되고, 배포가 끝나면 그 티켓이 사용자용 소식 한 줄로 옮겨진다. 우리 서버에 같은 걸
// 또 저장하면 두 벌을 손으로 맞춰야 하므로, 여기서는 **읽어다 보여주기만** 한다.
// (그래서 소식 문구를 고치는 데 이 앱을 다시 배포할 필요가 없다.)
//
// 키는 비밀이 아니다 — 앱 소스에 그대로 나가는 공개값이고, 진짜 담장은 ScreenChat 쪽에 등록된
// **도메인**이다(등록 안 된 주소에서 부르면 서버가 거절한다). 위젯 키와 같은 성격이다.
//
// 실패는 조용히 삼킨다. 소식을 못 불러오는 건 사용자가 하려던 일과 아무 상관이 없다 —
// 에러를 띄우면 "내 작업이 잘못됐나" 하고 놀라게 만들 뿐이다.

const ENDPOINT = "https://screen-chat.com/api/widget/whatsnew";
const WIDGET_KEY = "wk_7576bb381c935330e1080f64e1c98e755fda9d8df6d85d0b";

/** 마지막으로 확인한 소식의 시각. 이 값보다 새 소식이 있으면 "안 읽음"이다. */
const SEEN_KEY = "whatsNew:lastSeenAt";

export type WhatsNewCategory = "new" | "improve" | "fix";

/**
 * 이 소식이 어느 요청에서 나왔나 — ScreenChat 티켓 원문.
 *
 * 소식 본문(제목·불릿)은 "무엇이 좋아졌나"만 남기고 다듬은 글이라, **누가 왜 이걸 요청했는지가
 * 지워져 있다.** 그런데 이 앱을 쓰는 사람들에게는 그 대목이 오히려 반가운 정보다 — "아, 우리가
 * 불편하다고 올렸던 그거네" 하고 자기 요청이 반영된 걸 확인하는 자리가 되기 때문이다.
 * 그래서 요청자가 직접 쓴 원문을 함께 받아, 소식 아래에 **접어 두고** 원할 때만 펼치게 한다.
 *
 * 티켓 없이 우리가 직접 쓴 소식이면 `null` 이다.
 */
export type WhatsNewTicket = {
  /** 티켓 번호 — screen-chat.com 에서 그 요청을 가리키는 이름표 */
  no: number;
  /** 요청을 한 줄로 줄인 제목(요청자가 쓴 원문 그대로) */
  title: string;
  /** 요청 본문. 길다 — 화면에서 접어 둘 것 */
  request: string | null;
  requestedAt: string;
};

export type WhatsNewItem = {
  id: string;
  title: string;
  /** `- 한 줄` 형태의 불릿 2~3개 */
  summary: string;
  category: WhatsNewCategory;
  publishedAt: string;
  /** 요청자가 "여기가 불편해요" 하고 찍어 보낸 화면 (찍은 자리에 표시가 구워져 있다) */
  before: string[];
  /** 배포된 뒤 실제로 그렇게 바뀐 화면 */
  after: string[];
  /** 이 소식을 만든 요청(없을 수 있다 — 티켓 없이 우리가 직접 쓴 소식) */
  ticket: WhatsNewTicket | null;
};

export type WhatsNewFeed = { items: WhatsNewItem[]; latestPublishedAt: string | null };

export const CATEGORY_LABEL: Record<WhatsNewCategory, string> = {
  new: "새 기능",
  improve: "개선",
  fix: "오류 수정",
};

export async function fetchWhatsNew(signal?: AbortSignal): Promise<WhatsNewFeed> {
  const res = await fetch(ENDPOINT, { headers: { "X-Widget-Key": WIDGET_KEY }, signal });
  if (!res.ok) throw new Error(`whats-new ${res.status}`);
  const data = (await res.json()) as WhatsNewFeed;
  // `ticket` 은 나중에 붙은 칸이라, 아직 옛 응답을 주는 서버를 만나면 undefined 로 온다.
  // 화면이 "있음/없음"만 보게 여기서 null 로 못 박아 둔다(화면마다 undefined 를 또 따지지 않게).
  const items = (data.items ?? []).map((i) => ({ ...i, ticket: i.ticket ?? null }));
  return { items, latestPublishedAt: data.latestPublishedAt ?? null };
}

export function lastSeenAt(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

export function markSeen(latest: string | null) {
  if (!latest) return;
  try {
    localStorage.setItem(SEEN_KEY, latest);
  } catch {
    /* 사파리 프라이빗 모드 등 — 뱃지가 다시 뜰 뿐이라 그냥 넘어간다 */
  }
}

/**
 * 안 읽은 소식 수.
 *
 * **처음 온 사람에게는 0으로 친다.** 기록이 없다고 지난 소식 전부를 "안 읽음"으로 띄우면,
 * 앱을 처음 여는 사람이 빨간 뱃지 20개를 마주한다 — 알림이 아니라 잔소리가 된다.
 */
export function unreadCount(items: WhatsNewItem[]): number {
  const seen = lastSeenAt();
  if (!seen) return 0;
  return items.filter((i) => i.publishedAt > seen).length;
}

/** 기록이 없는 사람에게 "지금까지는 다 본 것"으로 도장을 찍는다(첫 방문 처리). */
export function seedSeen(latest: string | null) {
  if (!latest || lastSeenAt()) return;
  markSeen(latest);
}
